import PropTypes from 'prop-types';
import {
	Box, Modal, Tab, Tabs, useMediaQuery
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as CrossIcon } from '../../../Assets/Images/close-circle-grey.svg';
import { SALT_PAN } from '../../../Assets/Constants/theme.js';
import customToast from '../custom-toast.js';
import { getDownloadUrl } from '../../../Services/api-config.js';
import {
	downloadBase64File,
	downloadXlsxFile,
	getErrorMessage,
	getTruthyValue
} from '../../../utils/util.js';
import './pdf-pop-up-view.css';
import DetailsView from './details-view.js';
import DocumentViewer from './document-viewer.js';
import DetailsViewNew from './details-view-render-new.js';
import DownloadButtons from './download-buttons.js';

export const PdfPopUpView = ({
	docName,
	open,
	close,
	forwardToEntityBtn,
	DocUrl,
	rejectOnclick,
	mainTitle,
	showDetails = true,
	pdfHeaderList,
	data,
	forwardToEntityBtnAccept,
	ordertab = false,
	isEsignFlow = false
}) => {
	const pdfContainerRef = useRef();
	const divRef = useRef();
	const { showToast } = customToast();
	const isMobile = useMediaQuery('(max-width: 900px)');

	const [documentLoading, setDocumentLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [value, setValue] = useState(0);
	const [divHeightVal, setDivHeightVal] = useState(0);

	const handleChange = (event, newValue) => {
		setValue(newValue);
	};

	const [numPages, setNumPages] = useState(null);

	const handleScroll = () => {
		if (pdfContainerRef.current) {
			const scrollTop = pdfContainerRef.current.scrollTop;
			const pageHeight = pdfContainerRef.current.scrollHeight / numPages;
			const newPage = Math.floor(scrollTop / pageHeight) + 1;
			setCurrentPage(newPage);
		}
	};

	const onDocumentLoadSuccess = ({ numPages }) => {
		setNumPages(numPages);
		setCurrentPage(1);
	};

	const handleDownload = async (type) => {
		try {
			const response = await getDownloadUrl(type, data?.stampOrderId);
			if (response?.base64Data) {
				if (type === 'csv') {
					downloadBase64File(
						response?.base64Data?.base64Data || response.data,
						data?.stampOrderId,
						type,
						'application'
					);
				} else {
					downloadXlsxFile(
						response?.base64Data?.base64Data,
						data?.stampOrderId
					);
				}

				showToast('success', response?.base64Data?.message || '');
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
			return error;
		}
	};

	useEffect(() => {
		if (
			!DocUrl?.includes('/pdf;')
			&& !data?.stampPaperPdfPath?.includes('/pdf;')
		) {
			setDocumentLoading(false);
		}
	}, []);

	useEffect(() => {
		if (divRef) {
			const divHeight = divRef?.current?.clientHeight;
			setDivHeightVal(divHeight);
		}
	}, [divRef?.current]);

	const getCalculatedWidth = (showDetails, DocUrl, isMobile) => {
		if (showDetails) {
			return {};
		}

		if (DocUrl) {
			if (isMobile) {
				return {
					xs: '80%',
					md: '80%',
					lg: '80%',
					xl: '80%'
				};
			}

			if (DocUrl.includes('/pdf;')) {
				return {
					xs: 'fit-content',
					md: 'fit-content',
					lg: 'fit-content',
					xl: 'fit-content'
				};
			}

			return {
				xs: '600px',
				md: '600px',
				lg: '600px',
				xl: '600px'
			};
		}

		return {
			xs: 'fit-content',
			md: 'fit-content',
			lg: 'fit-content',
			xl: 'fit-content'
		};
	};

	const getCalculatedHeight = (isMobile, DocUrl) =>
		isMobile ? getTruthyValue(DocUrl, 'fit-content', '94vh') : 'auto';

	const getPadding = (isMobile) => (isMobile ? '6px 24px 24px 24px' : '24px');

	return (
		<Modal
			open={open}
			onClose={() => {
				setDocumentLoading(true);
				close();
			}}
			className='popUp-container'
		>
			<Box
				ref={divRef}
				className='popUp-content'
				sx={{
					width: getCalculatedWidth(showDetails, DocUrl, isMobile),
					height: getCalculatedHeight(isMobile, DocUrl),
					color: 'grey',
					padding: getPadding(isMobile),
					maxWidth: '100vw'
				}}
			>
				{isMobile && (
					<Box width={'100%'}>
						<Box
							sx={{
								ml: '24px',
								display: 'flex',
								justifyContent: 'center',
								marginBottom: '4px'
							}}
							onClick={() => {
								setDocumentLoading(true);
								close();
							}}
						>
							<CrossIcon
								style={{
									stroke: 'white',
									cursor: 'pointer',
									width: '24px',
									height: '24px'
								}}
							/>
						</Box>
						{showDetails && (
							<Tabs
								value={value}
								onChange={handleChange}
								indicatorColor='primary'
								textColor='inherit'
								variant='fullWidth'
								aria-label='full width tabs example'
								sx={{
									'& .MuiButtonBase-root': {
										textTransform: 'none',
										minHeight: '0px'
									},
									borderBottom: `1px solid ${SALT_PAN}`,
									'& .MuiTab-root.Mui-selected': {
										color: 'white'
									},
									ml: '24px'
								}}
							>
								<Tab label={isEsignFlow ? 'Document' : 'Stamp Papers'} />
								<Tab
									label={isEsignFlow ? 'Recipients Details' : 'Order Details'}
								/>
							</Tabs>
						)}
						{value === 0 && (
							<>
								<DocumentViewer
									data={data}
									docName={docName}
									DocUrl={DocUrl}
									numPages={numPages}
									currentPage={currentPage}
									showDetails={showDetails}
									isMobile={isMobile}
									documentLoading={documentLoading}
									setDocumentLoading={setDocumentLoading}
									onDocumentLoadSuccess={onDocumentLoadSuccess}
									handleScroll={handleScroll}
									View={<p>Loading...</p>}
									close={close}
									isEsignFlow={isEsignFlow}
								/>
								{ordertab && (
									<DownloadButtons handleDownload={handleDownload} />
								)}
							</>
						)}
						{value === 1 && (
							<DetailsView
								mainTitle={mainTitle}
								isMobile={isMobile}
								pdfHeaderList={pdfHeaderList}
								data={data}
								forwardToEntityBtn={forwardToEntityBtn}
								forwardToEntityBtnAccept={forwardToEntityBtnAccept}
								rejectOnclick={rejectOnclick}
							/>
						)}
					</Box>
				)}
				{!isMobile && (
					<>
						<Box display='flex'>
							<DocumentViewer
								data={data}
								docName={docName}
								DocUrl={DocUrl}
								numPages={numPages}
								currentPage={currentPage}
								showDetails={showDetails}
								isMobile={isMobile}
								documentLoading={documentLoading}
								setDocumentLoading={setDocumentLoading}
								onDocumentLoadSuccess={onDocumentLoadSuccess}
								handleScroll={handleScroll}
								View={<p>Loading...</p>}
								close={close}
								divHeightVal={divHeightVal}
								isEsignFlow={isEsignFlow}
							/>
							{showDetails && (
								<Box sx={{ position: 'relative' }}>
									<Box
										sx={{ display: 'flex', justifyContent: 'flex-end' }}
										onClick={() => {
											setDocumentLoading(true);
											close();
										}}
									>
										<CrossIcon
											style={{
												stroke: 'white',
												cursor: 'pointer',
												width: '24px',
												height: '24px'
											}}
										/>
									</Box>

									{!documentLoading && (
										<Box>
											{ordertab ? (
												<DetailsViewNew
													data={data}
													forwardToEntityBtn={forwardToEntityBtn}
													forwardToEntityBtnAccept={forwardToEntityBtnAccept}
													isMobile={isMobile}
													mainTitle={mainTitle}
													pdfHeaderList={pdfHeaderList}
													rejectOnclick={rejectOnclick}
												/>
											) : (
												<DetailsView
													mainTitle={mainTitle}
													isMobile={isMobile}
													pdfHeaderList={pdfHeaderList}
													data={data}
													forwardToEntityBtn={forwardToEntityBtn}
													forwardToEntityBtnAccept={forwardToEntityBtnAccept}
													rejectOnclick={rejectOnclick}
												/>
											)}
										</Box>
									)}
								</Box>
							)}
						</Box>
						{ordertab && <DownloadButtons handleDownload={handleDownload} />}
					</>
				)}
			</Box>
		</Modal>
	);
};

PdfPopUpView.propTypes = {
	docName: PropTypes.any,
	open: PropTypes.any,
	close: PropTypes.any,
	forwardToEntityBtn: PropTypes.any,
	DocUrl: PropTypes.any,
	rejectOnclick: PropTypes.any,
	mainTitle: PropTypes.any,
	showDetails: PropTypes.bool,
	pdfHeaderList: PropTypes.any,
	data: PropTypes.any,
	forwardToEntityBtnAccept: PropTypes.any,
	ordertab: PropTypes.any,
	isEsignFlow: PropTypes.bool
};
